import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, DatePicker, Divider, Empty, Form, notification, Popover, Row, Space, Table } from 'antd';
import dayjs from 'dayjs';

import { EyeOutlined } from '@ant-design/icons';
import { getToken } from '../../../security';
import request from '../../../services/request';
import { serializeParams } from '../../../common/helpers';
import { apiUrl } from '../../../app.config';
import DownloadPdfIcon from '../../../common/components/DownloadPdfIcon';
import DownloadJsonIcon from '../../../common/components/DownloadJsonIcon';

const { RangePicker } = DatePicker;

export default function DictumsTable({ distributionPoint, history }) {
  const [api, contextHolder] = notification.useNotification();
  const [loading, setLoading] = useState(false);
  const [dictums, setDictums] = useState([]);
  const [pagination, setPagination] = useState({});
  const [form] = Form.useForm();
  const isFetching = useRef(false);

  const filters = {
    dictumStartDate: dayjs().subtract(7, 'days').startOf('day').format('YYYY-MM-DD HH:mm:ss'),
    dictumEndDate: dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
    dictumDisplayPage: 0,
    dictumDisplayAmount: 10,
    distributionPointId: distributionPoint.id,
  };

  const fetchDictums = (params) => {
    setLoading(true);
    if (!distributionPoint.id || isFetching.current) return;
    isFetching.current = true;

    const queryParams = {
      page: params.dictumDisplayPage,
      size: params.dictumDisplayAmount,
      sort: 'dictumFolio,DESC',
    };

    request()
      .post(`dictum/getDictums${serializeParams(queryParams)}`, params)
      .then((response) => {
        setDictums(response.data.content);
        setPagination({ ...pagination, total: response.data.totalElements });
      })
      .catch((error) => {
        api.error({ message: 'Error fetching certificates:', description: error });
      })
      .finally(() => {
        setLoading(false);
        isFetching.current = false;
      });
  };

  useEffect(() => {
    if (distributionPoint.id) {
      fetchDictums(filters);
    }
  }, [distributionPoint]);

  const onChangePeriod = (period) => {
    const updatedFilters = {
      ...filters,
      dictumStartDate: period[0].format('YYYY-MM-DD HH:mm:ss'),
      dictumEndDate: period[1].format('YYYY-MM-DD HH:mm:ss'),
      distributionPointId: distributionPoint.id,
    };
    fetchDictums(updatedFilters);
  };

  const columns = [
    { title: 'Folio', dataIndex: 'dictumFolioDetail' },
    {
      title: 'Emisión del Dictamen',
      dataIndex: 'dictumIssueDateDetail',
      align: 'center',
    },
    {
      title: 'Fecha Muestra',
      dataIndex: 'dictumSampleTakenDetail',
      align: 'center',
    },
    {
      title: 'Fin Resultados',
      dataIndex: 'dictumTestResultDate',
      align: 'center',
    },
    {
      title: 'Método',
      dataIndex: 'dictumTestMethod'
    },
    {
      title: 'Resultado',
      dataIndex: 'dictumTestResult'
    },
    {
      title: 'Acciones',
      render: (_, record) => {
        const handleDownload = async (fileType) => {
          const dictumId = record.dictumId;
          if (!dictumId) return;

          try {
            setLoading(true);
            const url = `${apiUrl}dictum/${dictumId}/${fileType}`;
            const headers = {
              Authorization: getToken(),
              Accept: `application/${fileType}`
            };

            const response = await fetch(url, {
              method: 'GET',
              headers: headers
            });

            if (!response.ok) {
              throw new Error('Error al descargar el archivo');
            }

            const contentDisposition = response.headers.get('Content-Disposition') || response.headers.get('content-disposition');
            let fileName = `DI-${new Date().toISOString().split('T')[0]}.${fileType}`;

            if (contentDisposition) {
              const match = contentDisposition.match(/filename="(.+)"/);
              if (match && match[1]) {
                fileName = match[1];
              }
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(downloadUrl);
            document.body.removeChild(a);
          } catch (error) {
            api.error({ message: 'Error en la descarga:', description: error });
          }
          setLoading(false);
        };

        return (
          <Space>
            <Popover content="Descargar JSON">
              <Button icon={<DownloadJsonIcon />} onClick={() => handleDownload('json')} />
            </Popover>
            <Popover content="Descargar PDF">
              <Button icon={<DownloadPdfIcon />} onClick={() => handleDownload('pdf')} />
            </Popover>
            <Popover>
              <Button icon={<EyeOutlined />} onClick={() => history.push(`dictum/${record.dictumId}`)} />
            </Popover>
          </Space>
        );
      }
    },
  ];

  return (
    <>
      {contextHolder}
      <Form form={form} layout="vertical">
        <Row gutter={16} align="middle">
          <Col xs={24} sm={16}>
            <Form.Item
              name="period"
              label="Seleccionar periodo por fecha de dictamen"
              initialValue={[
                dayjs().subtract(7, 'days').startOf('day'),
                dayjs().endOf('day')
              ]}
              rules={[{ required: true, message: 'Por favor selecciona un periodo.' }]}
            >
              <RangePicker
                lang="es"
                format="YYYY/MM/DD"
                style={{ width: 300 }}
                onChange={onChangePeriod}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <Button type="primary" onClick={() => history.push('/dictum/new')}>
              Agregar Dictamen
            </Button>
          </Col>
        </Row>
      </Form>
      <Divider />
      <Table
        columns={columns}
        loading={loading}
        locale={{ emptyText: <Empty description="Sin registros" /> }}
        dataSource={dictums}
      />
    </>
  );
}
